import { getCookie } from '@api/HelperAPI';
import { useGetContext } from '@api/lendingAPI/lapiHooks';
import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';
import { TAccounts } from '@helpers/Accounts/accounts.types';
import { getAccountIsLoanEnded } from '@helpers/Accounts/getAccountIsLoanEnded';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import {
    fetchGetApplication,
    fetchGetCreditCardAccount
} from '@iwoca/lapi-client/edge';
import { useQuery } from '@tanstack/react-query';

import { getDefaultDashboardLocation } from './getDefaultDashboardLocation';
import { useStateKey } from '../../iwQueryClient/useStateKey';
import { CREDIT_CARD_BASE_URL } from '../creditCard/constants';
import { MY_OFFERS_URL } from '../myOffers/MyOffers.sagas';

type TDashboardRedirect = {
    redirectTo: Partial<Location> | undefined;
    activeFundedAccounts: TAccounts | undefined;
};

export const useGetDashboardRedirect = (): TDashboardRedirect => {
    const stateKey = useStateKey();
    const { data: context } = useGetContext();
    const isAccessUser = context?.logged_in_user_type === 'access_user';

    const { data: accountsResponse } = useQuery({
        ...getAccountsQueryConfig(stateKey),
        suspense: true
    });
    const { data: applicationResponse } = useQuery({
        queryKey: ['application', stateKey],
        queryFn: () =>
            fetchGetApplication({
                stateKey
            }),
        suspense: true
    });

    const { data: creditCardAccountResponse } = useQuery({
        queryKey: ['credit_card_account', stateKey],
        queryFn: () => fetchGetCreditCardAccount({ stateKey }),
        select: (data) => data.data.account,
        suspense: true,
        enabled: getCountry() === 'UK' && !isAccessUser
    });

    if (!accountsResponse || !accountsResponse.data || !applicationResponse) {
        throw new Error();
    }

    if (creditCardAccountResponse && creditCardAccountResponse.id) {
        return {
            redirectTo: { pathname: `${CREDIT_CARD_BASE_URL}/` },
            activeFundedAccounts: undefined
        };
    }

    const defaultDashboardLocation = getDefaultDashboardLocation(
        applicationResponse,
        accountsResponse
    );

    if (defaultDashboardLocation.pathname !== '/') {
        return {
            redirectTo: defaultDashboardLocation,
            activeFundedAccounts: undefined
        };
    }

    if (context?.logged_in_user_type === 'access_user') {
        return {
            redirectTo: { pathname: MY_OFFERS_URL },
            activeFundedAccounts: undefined
        };
    }

    const hasDashboardHomeCookieActive = getCookie('showDashboardHome');

    if (hasDashboardHomeCookieActive) {
        const activeFundedAccounts = accountsResponse.data.accounts.filter(
            (account) =>
                Boolean(account.account_id) && !getAccountIsLoanEnded(account)
        );

        return {
            redirectTo: undefined,
            activeFundedAccounts
        };
    }

    const latestActiveAccount = getLatestActiveAccount(
        accountsResponse.data.accounts
    );

    if (!latestActiveAccount) {
        throw new Error();
    }

    return {
        redirectTo: {
            pathname: `/dashboard/${latestActiveAccount.account_id}/`
        },
        activeFundedAccounts: undefined
    };
};

const getLatestActiveAccount = (accounts: TAccounts) => {
    const activeAccounts = accounts.filter((account: TAccounts[number]) =>
        [
            'credit_facility',
            'flexi24',
            'merchant_cash_advance',
            'term_loan'
        ].includes(account.product_type)
    );
    if (!activeAccounts) return null;

    const ongoingFlexiAccount = activeAccounts.find(
        (account: TAccounts[number]) =>
            account.account_id && account.loan_status !== 'ended'
    );
    if (ongoingFlexiAccount) return ongoingFlexiAccount;

    //temp: use latest_formal_offer to determine latest account
    const latestActiveAccount = activeAccounts.find(
        (account: TAccounts[number]) => Boolean(account.latest_formal_offer)
    );

    return latestActiveAccount;
};
